/**
 * Check-ins Tab Component
 * Displays a live feed of event check-ins with real-time updates via Supabase Realtime
 */

import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../services/supabase";
import {
  getEventAttendees,
  checkInAttendee,
  getEventPendingInvites,
} from "../../../services/events.service";
import { formatDate, formatTime } from "../../../utils/formatters";
import LoadingSpinner from "../../common/LoadingSpinner";
import type { UUID } from "../../../types/common.types";

interface CheckinsTabProps {
  eventId: UUID;
}

interface Attendee {
  id: string;
  event_id: string;
  contact_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  attendance_status: string;
  checked_in_at: string | null;
  checkin_code: string | null;
  contacts?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

interface CheckinEvent {
  id: string;
  attendeeName: string;
  email: string;
  checkedInAt: string;
  isNew?: boolean;
}

export default function CheckinsTab({ eventId }: CheckinsTabProps) {
  const queryClient = useQueryClient();
  const [recentCheckins, setRecentCheckins] = useState<CheckinEvent[]>([]);
  const [manualCheckInId, setManualCheckInId] = useState<string | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  // Fetch all attendees
  const { data: attendees = [], isLoading } = useQuery({
    queryKey: ["event-attendees", eventId],
    queryFn: () => getEventAttendees(eventId),
  });

  // Item 2: pending guest invites (minted, not yet confirmed — no attendee row yet, so invisible in
  // the roster above). Surfaced so an operator can see an invite was sent and is awaiting confirm.
  const { data: pendingInvites = [] } = useQuery({
    queryKey: ["event-pending-invites", eventId],
    queryFn: () => getEventPendingInvites(eventId),
  });

  // Calculate stats
  const totalRegistered = attendees.filter(
    (a: Attendee) =>
      a.attendance_status === "registered" ||
      a.attendance_status === "attended",
  ).length;
  const checkedIn = attendees.filter((a: Attendee) => a.checked_in_at).length;
  const pending = totalRegistered - checkedIn;

  // Get display name for attendee
  const getAttendeeName = (attendee: Attendee): string => {
    if (attendee.contacts) {
      return `${attendee.contacts.first_name || ""} ${attendee.contacts.last_name || ""}`.trim();
    }
    return (
      `${attendee.first_name || ""} ${attendee.last_name || ""}`.trim() ||
      "Unknown"
    );
  };

  const getAttendeeEmail = (attendee: Attendee): string => {
    return attendee.contacts?.email || attendee.email || "";
  };

  // Initialize recent check-ins from existing data
  useEffect(() => {
    if (attendees.length > 0) {
      const checkedInAttendees = attendees
        .filter((a: Attendee) => a.checked_in_at)
        .sort(
          (a: Attendee, b: Attendee) =>
            new Date(b.checked_in_at!).getTime() -
            new Date(a.checked_in_at!).getTime(),
        )
        .slice(0, 20)
        .map((a: Attendee) => ({
          id: a.id,
          attendeeName: getAttendeeName(a),
          email: getAttendeeEmail(a),
          checkedInAt: a.checked_in_at!,
          isNew: false,
        }));
      setRecentCheckins(checkedInAttendees);
    }
  }, [attendees]);

  // Set up Supabase Realtime subscription for check-ins
  useEffect(() => {
    const channel = supabase
      .channel(`event-checkins-${eventId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "event_attendees",
          filter: `event_id=eq.${eventId}`,
        },
        async (payload) => {
          const updatedAttendee = payload.new as Attendee;

          // Only react to check-ins (when checked_in_at is newly set)
          if (updatedAttendee.checked_in_at && !payload.old?.checked_in_at) {
            // Refetch attendees to get full data
            queryClient.invalidateQueries({
              queryKey: ["event-attendees", eventId],
            });

            // Add to recent check-ins with animation flag
            const newCheckin: CheckinEvent = {
              id: updatedAttendee.id,
              attendeeName:
                `${updatedAttendee.first_name || ""} ${updatedAttendee.last_name || ""}`.trim() ||
                "Unknown",
              email: updatedAttendee.email || "",
              checkedInAt: updatedAttendee.checked_in_at,
              isNew: true,
            };

            setRecentCheckins((prev) => {
              // Prevent duplicates
              const filtered = prev.filter((c) => c.id !== newCheckin.id);
              return [newCheckin, ...filtered].slice(0, 20);
            });

            // Remove "new" flag after animation
            setTimeout(() => {
              setRecentCheckins((prev) =>
                prev.map((c) =>
                  c.id === newCheckin.id ? { ...c, isNew: false } : c,
                ),
              );
            }, 3000);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, queryClient]);

  // Manual check-in handler
  const handleManualCheckIn = async (attendeeId: string) => {
    setIsCheckingIn(true);
    setManualCheckInId(attendeeId);
    try {
      await checkInAttendee(attendeeId);
      queryClient.invalidateQueries({ queryKey: ["event-attendees", eventId] });
    } catch (error) {
      console.error("Error checking in attendee:", error);
    } finally {
      setIsCheckingIn(false);
      setManualCheckInId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards — tighter gap + smaller numbers on phones so 3-across doesn't cramp */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-center">
          <div className="text-2xl sm:text-3xl font-bold text-blue-600">
            {totalRegistered}
          </div>
          <div className="text-sm text-blue-600">Registered</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 sm:p-4 text-center">
          <div className="text-2xl sm:text-3xl font-bold text-green-600">
            {checkedIn}
          </div>
          <div className="text-sm text-green-600">Checked In</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3 sm:p-4 text-center">
          <div className="text-2xl sm:text-3xl font-bold text-yellow-600">
            {pending}
          </div>
          <div className="text-sm text-yellow-600">Pending</div>
        </div>
      </div>

      {/* Item 1(b): checking in here is ADMISSION only. Adjusting a ticket's reward value lives on
          the Operations tab — say so here so an operator doesn't expect to do it in this list. */}
      <p className="text-xs text-gray-500 flex items-start gap-1.5 px-0.5">
        <span aria-hidden="true">ℹ️</span>
        <span>
          Checking in here records attendance. To add or increase a
          ticket&apos;s reward value, use the{" "}
          <span className="font-medium text-gray-700">Operations</span> tab.
        </span>
      </p>

      {/* Item 2: Pending guest invites — minted invites awaiting the guest's confirmation. These have
          no attendee row yet, so they don't appear in the roster/feed below. */}
      {pendingInvites.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <h3 className="font-medium text-amber-900">
              Pending guest invites
            </h3>
            <span className="text-sm text-amber-700">
              {pendingInvites.length} awaiting confirmation
            </span>
          </div>
          <ul className="divide-y divide-gray-100">
            {pendingInvites.map((inv) => {
              const name = [inv.guest_first_name, inv.guest_last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
              return (
                <li
                  key={inv.token_id}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {name || inv.guest_email || "Guest"}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {inv.guest_email}
                      {inv.inviter_name
                        ? ` · guest of ${inv.inviter_name}`
                        : ""}
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5">
                    Invited {formatDate(inv.created_at)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Live Check-in Feed */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <h3 className="font-medium text-gray-900">Live Check-ins</h3>
          </div>
          <span className="text-sm text-gray-500">
            {recentCheckins.length} recent
          </span>
        </div>

        {recentCheckins.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
            <p>No check-ins yet</p>
            <p className="text-sm mt-1">
              Check-ins will appear here in real-time
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {recentCheckins.map((checkin) => (
              <div
                key={checkin.id}
                className={`px-4 py-3 flex items-center justify-between transition-all duration-500 ${
                  checkin.isNew ? "bg-green-50 border-l-4 border-green-500" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      checkin.isNew ? "bg-green-500" : "bg-green-100"
                    }`}
                  >
                    <svg
                      className={`w-5 h-5 ${checkin.isNew ? "text-white" : "text-green-600"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {checkin.attendeeName}
                    </div>
                    <div className="text-sm text-gray-500">{checkin.email}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-900">
                    {formatTime(checkin.checkedInAt)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(checkin.checkedInAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Attendees */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Pending Check-ins</h3>
        </div>

        {pending === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">
            <svg
              className="mx-auto h-10 w-10 text-green-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>All registered attendees have checked in!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {attendees
              .filter(
                (a: Attendee) =>
                  !a.checked_in_at &&
                  (a.attendance_status === "registered" ||
                    a.attendance_status === "accepted"),
              )
              .map((attendee: Attendee) => (
                <div
                  key={attendee.id}
                  className="px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {getAttendeeName(attendee)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {getAttendeeEmail(attendee)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleManualCheckIn(attendee.id)}
                    disabled={isCheckingIn && manualCheckInId === attendee.id}
                    className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                  >
                    {isCheckingIn && manualCheckInId === attendee.id ? (
                      <span className="flex items-center gap-1">
                        <svg
                          className="animate-spin h-4 w-4"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Checking in...
                      </span>
                    ) : (
                      "Check In"
                    )}
                  </button>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Check-in Scanner Link */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">QR Code Scanner</h3>
            <p className="text-primary-100 text-sm mt-1">
              Use the dedicated scanner page for faster check-ins
            </p>
          </div>
          <a
            href="/event-checkin"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-white text-primary-700 font-medium rounded-lg hover:bg-primary-50 transition-colors"
          >
            Open Scanner
          </a>
        </div>
      </div>
    </div>
  );
}
